/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useColorModeValue, useColorMode } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

/**
 * Custom hook to unify theme and language across all admin dashboard pages
 */
export function useTheme() {
  const { i18n } = useTranslation();
  const { colorMode, toggleColorMode } = useColorMode();
  const isArabic = i18n.language === "ar";
  const isDark = colorMode === "dark";

  // Set document direction based on language
  useEffect(() => {
    document.documentElement.dir = isArabic ? "rtl" : "ltr";
    document.documentElement.lang = isArabic ? "ar" : "en";
  }, [isArabic]);

  // Unified color values - Beautiful and consistent colors
  const colors = {
    // Background colors
    bg: useColorModeValue("#f8fafc", "#0f172a"),
    cardBg: useColorModeValue("white", "#1e293b"),

    // Text colors
    textColor: useColorModeValue("#1e293b", "#f1f5f9"),
    subText: useColorModeValue("#64748b", "#94a3b8"),

    // Main accent color (Purple for light, Gold for dark)
    mainColor: useColorModeValue("#6A11CB", "#FFD700"),
    mainColorHover: useColorModeValue("#5b0fb3", "#ffed4e"),
    mainColorLight: useColorModeValue("#8b5cf6", "#fef08a"),

    // Border and divider colors
    borderColor: useColorModeValue("#e2e8f0", "#334155"),
    dividerColor: useColorModeValue("#e5e7eb", "#475569"),

    // Hover and interactive colors
    hoverBg: useColorModeValue("gray.50", "gray.700"),
    activeBg: useColorModeValue("purple.50", "yellow.900"),

    // Table colors
    tableHeaderBg: useColorModeValue("gray.50", "#1e293b"),
    tableRowHover: useColorModeValue("purple.50", "#334155"),
    tableBorder: useColorModeValue("#e5e7eb", "#475569"),

    // Button colors
    buttonBg: useColorModeValue("#6A11CB", "#FFD700"),
    buttonHover: useColorModeValue("#5b0fb3", "#ffed4e"),
    buttonText: useColorModeValue("white", "#1e293b"),

    // Badge colors - brighter in dark mode for better visibility
    badgeSuccess: useColorModeValue("green.100", "green.400"),
    badgeError: useColorModeValue("red.100", "red.400"),
    badgeWarning: useColorModeValue("yellow.100", "yellow.400"),
    badgeInfo: useColorModeValue("blue.100", "blue.400"),
    badgeSuccessText: useColorModeValue("green.800", "green.900"),
    badgeErrorText: useColorModeValue("red.800", "red.900"),

    // Input colors - darker in dark mode
    inputBg: useColorModeValue("white", "#1e293b"),
    inputBorder: useColorModeValue("#d1d5db", "#475569"),
    inputFocus: useColorModeValue("#6A11CB", "#FFD700"),
  };

  return {
    isArabic,
    isDark,
    colorMode,
    toggleColorMode,
    colors,
    dir: isArabic ? "rtl" : "ltr",
    textAlign: isArabic ? "right" : "left",
  };
}

